import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface BreadcrumbProps {
  children: ReactNode;
}

interface BreadcrumbItemProps {
  children: ReactNode;
  to?: string;
}

/**
 * Breadcrumb component for displaying navigation hierarchy.
 *
 * When depth > 3, collapses middle items to "..." linking to the last collapsed item.
 *
 * Example:
 * <Breadcrumb>
 *   <Breadcrumb.Item to="/cases">Cases</Breadcrumb.Item>
 *   <Breadcrumb.Item>Case Details</Breadcrumb.Item>
 * </Breadcrumb>
 *
 * With 4 items: [A, B, C, D] becomes [A, ..., C, D] where ... links to B
 * With 3 items: [A, B, C] shows all items without collapsing
 */
export function Breadcrumb({ children }: BreadcrumbProps) {
  const childrenArray = React.Children.toArray(children);

  // If depth <= 3, show all items (collapsing only makes sense with 4+ items)
  if (childrenArray.length <= 3) {
    return (
      <div className="flex items-center gap-[2px]">
        {childrenArray.map((child, index) => (
          <React.Fragment key={index}>
            {child}
            {index < childrenArray.length - 1 && <span className="text-border">/</span>}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Depth > 3: Show first item, "...", and last 2 items
  const firstItem = childrenArray[0];
  const lastTwoItems = childrenArray.slice(-2);

  // Get the link from the last collapsed item (the item before last 2)
  const lastCollapsedItem = childrenArray[childrenArray.length - 3];
  const lastCollapsedLink = React.isValidElement(lastCollapsedItem)
    ? (lastCollapsedItem.props as BreadcrumbItemProps).to
    : undefined;

  return (
    <div className="flex items-center gap-[2px]">
      {firstItem}
      <span className="text-border">/</span>

      {/* Ellipsis item linking to the last collapsed breadcrumb */}
      {lastCollapsedLink ? (
        <Link
          to={lastCollapsedLink}
          className="px-1 text-sm font-normal text-foreground hover:underline"
        >
          ...
        </Link>
      ) : (
        <span className="text-sm px-1">...</span>
      )}
      <span className="text-border">/</span>

      {lastTwoItems.map((child, index) => (
        <React.Fragment key={`last-${index}`}>
          {child}
          {index < lastTwoItems.length - 1 && <span className="text-border">/</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function BreadcrumbItem({ children, to }: BreadcrumbItemProps) {
  if (to) {
    // Clickable breadcrumb
    return (
      <Link to={to} className="px-1 text-sm font-normal text-foreground hover:underline">
        {children}
      </Link>
    );
  }

  // Current page (non-clickable)
  return <span className="text-sm px-1">{children}</span>;
}

Breadcrumb.Item = BreadcrumbItem;
